import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loading } from "@/components/Common";
import { ConflictDialog } from "@/components/ConflictDialog";
import { Card, Fld, Sel, Txt } from "@/components/returns/RetCommon";
import { api, errMsg, isConflict } from "@/lib/api";

const ListEditor = ({ k, list, onChange }) => {
  const [v, setV] = useState("");
  return (
    <Card title={k.replace(/_/g, " ")} testid={`list-${k}`}>
      <div className="flex flex-wrap gap-2">{list.map((o, i) => (
        <button key={o.value} onClick={() => onChange(list.map((x, j) => (j === i ? { ...x, active: !x.active } : x)))} title="Click to activate / deactivate"
          className={`rounded-full border px-2.5 py-1 text-xs ${o.active ? "bg-white" : "bg-muted text-muted-foreground line-through"}`} data-testid={`opt-${k}-${o.value}`}>{o.value}</button>))}</div>
      <div className="mt-3 flex gap-2"><Input value={v} onChange={(e) => setV(e.target.value)} placeholder="Add option" className="h-8" data-testid={`opt-add-${k}`} />
        <Button size="sm" variant="outline" disabled={!v.trim() || list.some((o) => o.value === v.trim())} onClick={() => { onChange([...list, { value: v.trim(), active: true }]); setV(""); }}><Plus className="h-3.5 w-3.5" /></Button></div>
    </Card>
  );
};

const Importer = () => {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); setBusy(true); setDone(null);
    try { setPrev((await api.post("/returns-import/preview", fd)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { setDone((await api.post("/returns-import/commit", { token: prev.token })).data); setPrev(null); toast.success("Import finished"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Import returns (CSV)" testid="ret-import">
      <p className="mb-2 text-xs text-muted-foreground">Upsert by return_id — existing returns are updated, never duplicated. Imported rows never post stock.</p>
      <input type="file" accept=".csv" disabled={busy} onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="ret-import-file" />
      {prev && <div className="mt-3 space-y-2 text-sm" data-testid="ret-import-preview">
        <div><b>{prev.rows}</b> rows ready · <b>{prev.new}</b> new · <b>{prev.updates}</b> updates · <b className="text-red-600">{prev.failed_count}</b> failed</div>
        <div className="flex flex-wrap gap-1 text-xs">{Object.entries(prev.by_status).map(([s, n]) => <span key={s} className="rounded bg-muted px-2 py-0.5">{s}: <b>{n}</b></span>)}</div>
        {prev.failed.length > 0 && <ul className="max-h-32 overflow-auto text-xs text-red-700">{prev.failed.map((f) => <li key={f}>{f}</li>)}</ul>}
        <Button size="sm" onClick={commit} disabled={busy || !prev.rows} data-testid="ret-import-commit">Import {prev.rows} rows</Button></div>}
      {done && <div className="mt-3 text-sm" data-testid="ret-import-summary">Done: <b>{done.inserted}</b> added, <b>{done.updated}</b> updated.</div>}
    </Card>
  );
};

export default function ReturnSettings() {
  const [s, setS] = useState(null);
  const [conflict, setConflict] = useState(false);
  const load = () => api.get("/returns/meta").then((r) => setS(r.data.settings));
  useEffect(() => { load(); }, []);
  if (!s) return <Loading />;
  const save = async () => {
    try { const { data } = await api.put("/returns-settings", { ...s, expected_rev: s.rev || 0 }); setS(data); toast.success("Return settings saved"); }
    catch (e) { if (isConflict(e)) setConflict(true); else toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="return-settings">
      <PageHeader title="Return Settings" subtitle="Dropdown lists, ageing limits, stock posting and import. Options in use can be deactivated, never deleted.">
        <Button size="sm" onClick={save} data-testid="ret-settings-save">Save settings</Button>
      </PageHeader>
      <div className="grid gap-3 md:grid-cols-3">
        <Card title="Ageing limits (days)"><div className="grid grid-cols-2 gap-3">
          <Fld label="Amber after"><Txt type="number" value={s.amber_days} onChange={(v) => setS({ ...s, amber_days: v })} testid="ret-amber" /></Fld>
          <Fld label="Red after"><Txt type="number" value={s.red_days} onChange={(v) => setS({ ...s, red_days: v })} testid="ret-red" /></Fld></div></Card>
        <Card title="Stock posting"><label className="flex items-center gap-3 text-sm"><Switch checked={s.post_stock} onCheckedChange={(v) => setS({ ...s, post_stock: v })} data-testid="ret-post-stock" />
          Post restocked returns to Daily IN/OUT</label>
          <div className="mt-3"><Fld label="Restock into warehouse"><Sel value={s.restock_warehouse} onChange={(v) => setS({ ...s, restock_warehouse: v || "A" })} options={["A", "B"]} testid="ret-restock-wh" /></Fld></div>
          <p className="mt-2 text-[11px] text-muted-foreground">Off by default. When on, a "Return Restock" IN is created once, and reversed if the return is un-restocked.</p></Card>
        <Importer />
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{Object.entries(s.lists).map(([k, list]) => (
        <ListEditor key={k} k={k} list={list} onChange={(l) => setS({ ...s, lists: { ...s.lists, [k]: l } })} />))}</div>
      <ConflictDialog open={conflict} what="setting" onClose={() => setConflict(false)} onReload={() => { setConflict(false); load(); }} />
    </div>
  );
}
